import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { StudyResults } from "@/types/api";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import {
  formatParameterValue,
  getParameterDisplayName,
} from "@/utils/parameterNames";
import {
  Activity,
  TrendingUp,
  Scissors,
  TrendingDown,
} from "lucide-react";
import { useMemo, useState, useCallback, useRef } from "react";
import React from "react";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

interface OptimizationHistoryChartProps {
  results: StudyResults;
  className?: string;
  pointLimit?: number;
  onPointLimitChange?: (limit: number) => void;
  totalTrialsCount?: number; // Total trials available (before slicing)
}

// X-axis is always trial number (removed datetime options per user request)
type TrialState = "COMPLETE" | "PRUNED" | "FAIL" | "RUNNING" | "WAITING";

interface ProcessedTrial {
  number: number;
  value?: number;
  params: Record<string, number | string | boolean>;
  datetime_start?: string;
  datetime_complete?: string;
  duration?: number;
  state?: string;
  x_value: number | string;
  display_time?: string | undefined;
  state_color: string;
  state_label: string;
  is_best_so_far: boolean;
  improvement_pct: number;
  cancellation_reason: string | null;
  rank?: number; // rank among completed trials (1 = best). undefined for incomplete
}

interface HistoryChartData {
  feasible_trials: ProcessedTrial[];
  infeasible_trials: ProcessedTrial[];
  best_value_line: Array<{
    x_value: number | string;
    best_value: number;
    trial_number: number;
  }>;
}

const TRIAL_STATE_COLORS = {
  COMPLETE: "#10b981", // emerald-500
  PRUNED: "#64748b", // slate-500
  FAIL: "#ef4444", // red-500
  RUNNING: "#f59e0b", // amber-500
  WAITING: "#6b7280", // gray-500
} as const;

const TRIAL_STATE_LABELS = {
  COMPLETE: "Complete",
  PRUNED: "Pruned",
  FAIL: "Failed",
  RUNNING: "Running",
  WAITING: "Waiting",
} as const;

// Ranking tie behavior: 'competition' => 1,1,3 ; 'dense' => 1,1,2
const RANK_TIE_MODE: 'competition' | 'dense' = 'competition';

// Convert 1 -> 1st, 2 -> 2nd, 3 -> 3rd, 4 -> 4th, ...
function toOrdinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

// Tooltip layout tuning
const TOOLTIP_OFFSET = 12;     // gap from the point
const TOOLTIP_MARGIN = 8;      // min margin to chart edges
const DEFAULT_TOOLTIP_SIZE = { width: 280, height: 180 }; // first-hover fallback

// Enhanced reason derivation for why trials were cancelled/failed
function deriveCancellationReason(
  state: TrialState,
  trial: any,
  index: number,
  allTrials: any[]
): string | null {
  switch (state) {
    case "PRUNED":
      // Pruned trials are stopped early due to poor performance
      if (trial?.duration && trial.duration < 10) {
        return "Early termination due to poor initial performance";
      }
      if (index > 0 && allTrials && Array.isArray(allTrials)) {
        const previousTrials = allTrials.slice(0, index);
        const completedTrials = previousTrials.filter(t => t?.duration && t?.state === "COMPLETE");
        const completedCount = completedTrials.length;

        if (completedCount > 0) {
          const totalDuration = completedTrials.reduce((sum, t) => sum + (t.duration || 0), 0);
          const avgDuration = totalDuration / completedCount;

          if (trial?.duration && avgDuration && trial.duration < avgDuration * 0.3) {
            return "Pruned due to underperformance compared to previous trials";
          }
        }
      }
      return "Automatically pruned by optimization algorithm";
      
    case "FAIL":
      // Different types of failures
      if (trial?.duration && trial.duration < 5) {
        return "Failed during initialization or parameter validation";
      }
      if (trial?.params && typeof trial.params === "object") {
        const paramValues = Object.values(trial.params).filter(val => val != null);
        const hasExtremeValues = paramValues.some((val) =>
          typeof val === "number" && (Math.abs(val) > 1000000 || val === 0)
        );
        if (hasExtremeValues) {
          return "Failed due to extreme parameter values";
        }
      }
      return "Execution failed due to runtime error or timeout";
      
    case "WAITING":
      return "Trial is queued and waiting for resources";
      
    case "RUNNING":
      const now = Date.now();
      if (trial?.datetime_start) {
        try {
          const startTime = new Date(trial.datetime_start).getTime();
          if (!isNaN(startTime)) {
            const runningTime = (now - startTime) / 1000; // seconds
            if (runningTime > 300) { // More than 5 minutes
              return "Long-running trial - may be processing complex calculations";
            }
          }
        } catch {
          // Invalid date, ignore
        }
      }
      return "Currently executing optimization";
      
    case "COMPLETE":
      return null; // No reason needed for successful completion
      
    default:
      return null;
  }
}

export function OptimizationHistoryChart({
  results,
  className,
  pointLimit = 100,
  onPointLimitChange,
  totalTrialsCount,
}: OptimizationHistoryChartProps) {
  // Control states
  const [includePruned, setIncludePruned] = useState(false); // Default: exclude pruned trials
  const [includeNegativeValues, setIncludeNegativeValues] = useState(false); // Default: exclude negative values
  const [markerSize, setMarkerSize] = useState<number>(6);

  // Calculate total trials available (max possible to display)
  // Use prop if provided (from parent), otherwise calculate from results
  const totalTrialsAvailable = useMemo(() => {
    if (totalTrialsCount !== undefined) {
      return Math.max(1, totalTrialsCount);
    }
    const trials = results?.trials || results?.top_trials || [];
    return Math.max(1, Array.isArray(trials) ? trials.length : 0);
  }, [results, totalTrialsCount]);
  
  // Mouse position (for crosshairs)
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  // Pixel-perfect hover state: only set when the pointer is directly on a rendered dot
  type HoveredPoint = { cx: number; cy: number; point: any };
  const [hovered, setHovered] = useState<HoveredPoint | null>(null);

  // Track tooltip measured size so we can clamp precisely
  const [tooltipSize, setTooltipSize] = useState<{ width: number; height: number }>(DEFAULT_TOOLTIP_SIZE);
  // Ref to the chart wrapper (the element that contains the recharts wrapper)
  const chartRef = useRef<HTMLDivElement | null>(null);

  // Update tooltip size only when it really changed (prevents re-render loops)
  const updateTooltipSize = useCallback(
    (next: { width: number; height: number }) => {
      setTooltipSize((prev) => {
        const w = Math.round(next.width);
        const h = Math.round(next.height);
        if (Math.round(prev.width) === w && Math.round(prev.height) === h) return prev;
        return { width: w, height: h };
      });
    },
    []
  );

  // Compute a clamped tooltip position so it never leaves the chart area
  const computeTooltipPosition = useCallback(
    (cx: number, cy: number) => {
      const wrapper = chartRef.current;
      const W = wrapper?.clientWidth ?? 0;
      const H = wrapper?.clientHeight ?? 0;
      const tw = tooltipSize.width || DEFAULT_TOOLTIP_SIZE.width;
      const th = tooltipSize.height || DEFAULT_TOOLTIP_SIZE.height;

      // Prefer right side; flip left if needed
      let x = cx + TOOLTIP_OFFSET;
      if (x + tw + TOOLTIP_MARGIN > W) {
        x = cx - tw - TOOLTIP_OFFSET;
      }
      // Prefer above the point; move below if not enough space
      let y = cy - th - TOOLTIP_OFFSET;
      if (y < TOOLTIP_MARGIN) {
        y = cy + TOOLTIP_OFFSET;
      }

      // Final clamps to ensure we stay inside
      x = Math.min(Math.max(TOOLTIP_MARGIN, x), Math.max(TOOLTIP_MARGIN, W - tw - TOOLTIP_MARGIN));
      y = Math.min(Math.max(TOOLTIP_MARGIN, y), Math.max(TOOLTIP_MARGIN, H - th - TOOLTIP_MARGIN));

      return { x, y };
    },
    [tooltipSize],
  );

  // Determine objective direction from the study results
  function resolveDirection(results: StudyResults): "maximize" | "minimize" {
    // 1) explicit single-objective direction
    const d1 = (results as any)?.direction;
    if (typeof d1 === "string") {
      const k = d1.toLowerCase();
      if (k.startsWith("max")) return "maximize";
      if (k.startsWith("min")) return "minimize";
    }
    // 2) multi-objective style: directions[0]
    const d2 = (results as any)?.directions?.[0];
    if (typeof d2 === "string") {
      const k = d2.toLowerCase();
      if (k.startsWith("max")) return "maximize";
      if (k.startsWith("min")) return "minimize";
    }
    // 3) some APIs use a boolean flag
    if ((results as any)?.is_lower_better === true) return "minimize";
    if ((results as any)?.is_lower_better === false) return "maximize";

    // 4) last-resort inference from data
    const trials = results?.trials || results?.top_trials || [];
    const vals = (Array.isArray(trials) ? trials : [])
      .filter((t) => t?.state === "COMPLETE" && typeof t?.value === "number")
      .map((t) => t.value as number);
    if (vals.length) {
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const best = results.best_value;
      if (typeof best === "number") {
        const closerToMin = Math.abs(best - min) <= Math.abs(best - max);
        return closerToMin ? "minimize" : "maximize";
      }
      // If we can't tell, prefer maximize for trading metrics
      return "maximize";
    }
    return "maximize";
  }

  const objectiveDirection = useMemo(
    () => resolveDirection(results),
    [results],
  );
  const isMinimization = objectiveDirection === "minimize";

  // Process trials data based on current settings
  const processedData: HistoryChartData = useMemo(() => {
    // Prefer full trials list over limited top_trials
    const sourceTrials = results?.trials || results?.top_trials || [];

    if (!Array.isArray(sourceTrials) || sourceTrials.length === 0) {
      return {
        feasible_trials: [],
        infeasible_trials: [],
        best_value_line: [],
      };
    }

    // Get all trials first
    const allTrials = [...sourceTrials];

    // Filter based on includePruned setting
    const trials = includePruned
      ? allTrials
      : allTrials.filter((trial) => {
          const state = (trial.state as TrialState) || "COMPLETE";
          return state !== "PRUNED";
        });

    // Sort by trial number for proper ordering
    trials.sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0));

    const processedTrials: ProcessedTrial[] = trials.map((trial, index) => {
      if (!trial) {
        return {
          number: index,
          x_value: index,
          state_color: TRIAL_STATE_COLORS.FAIL,
          state_label: TRIAL_STATE_LABELS.FAIL,
          is_best_so_far: false,
          improvement_pct: 0,
          cancellation_reason: "Invalid trial data",
          params: {},
        } as ProcessedTrial;
      }

      const state = (trial.state as TrialState) || "COMPLETE";

      // X value is always trial number
      const x_value: number = trial.number ?? index;
      const display_time: string | undefined = undefined;

      // Calculate if this trial was best so far
      const previousTrials = trials.slice(0, index);
      const completedPreviousTrials = previousTrials.filter(
        (t) => t && (t.state as TrialState) === "COMPLETE",
      );

      let is_best_so_far = false;
      let improvement_pct = 0;

      if (state === "COMPLETE" && typeof trial.value === "number") {
        if (completedPreviousTrials.length === 0) {
          is_best_so_far = true;
        } else {
          const validPreviousValues = completedPreviousTrials
            .map((t) => t?.value)
            .filter((v): v is number => typeof v === "number");

          if (validPreviousValues.length > 0) {
            const previousBest = isMinimization
              ? Math.min(...validPreviousValues)
              : Math.max(...validPreviousValues);

            is_best_so_far = isMinimization
              ? trial.value < previousBest
              : trial.value > previousBest;

            if (is_best_so_far && previousBest !== 0) {
              improvement_pct =
                Math.abs((trial.value - previousBest) / previousBest) * 100;
            }
          } else {
            // No valid previous values, so this is the first valid one
            is_best_so_far = true;
          }
        }
      }

      const cancellation_reason = deriveCancellationReason(state, trial, index, trials);

      return {
        ...trial,
        x_value,
        display_time,
        state_color: TRIAL_STATE_COLORS[state],
        state_label: TRIAL_STATE_LABELS[state],
        is_best_so_far,
        improvement_pct,
        cancellation_reason,
      };
    });

    // -----------------------
    // RANKING (completed only)
    // -----------------------
    // Quantize values to avoid floating point issues when grouping ties.
    const quant = (v: number) => v.toFixed(12);

    // Gather completed trials with numeric values
    const completedForRanking = processedTrials.filter(
      (t) => t.state === "COMPLETE" && typeof t.value === "number"
    ) as Array<ProcessedTrial & { value: number }>;

    // Count occurrences per quantized value (for tie sizes)
    const countsByKey = new Map<string, number>();
    for (const t of completedForRanking) {
      const k = quant(t.value);
      countsByKey.set(k, (countsByKey.get(k) || 0) + 1);
    }

    // Sort unique value keys by objective direction
    const sortedKeys = Array.from(countsByKey.keys()).sort((ka, kb) => {
      const a = parseFloat(ka);
      const b = parseFloat(kb);
      return isMinimization ? a - b : b - a;
    });

    // Assign ranks by value key
    const rankByKey = new Map<string, number>();
    let nextRank = 1;
    for (const key of sortedKeys) {
      rankByKey.set(key, nextRank);
      nextRank += (RANK_TIE_MODE === "competition" ? (countsByKey.get(key) || 1) : 1);
    }

    // Write rank back onto processedTrials
    for (const t of processedTrials) {
      if (t.state === "COMPLETE" && typeof t.value === "number") {
        const foundRank = rankByKey.get(quant(t.value));
        if (foundRank !== undefined) {
          t.rank = foundRank;
        }
      }
      // Leave rank as undefined for incomplete trials or when not found
    }

    // Separate feasible and infeasible trials
    // Also apply negative value filtering if enabled
    const feasible_trials = processedTrials.filter((t) => {
      const isComplete = (t.state as TrialState) === "COMPLETE";
      const hasValue = typeof t.value === "number";
      if (!isComplete || !hasValue) return false;
      // Filter out negative values unless includeNegativeValues is true
      if (!includeNegativeValues && typeof t.value === "number" && t.value < 0) return false;
      return true;
    });
    const infeasible_trials = processedTrials.filter(
      (t) =>
        (t.state as TrialState) !== "COMPLETE" || typeof t.value !== "number",
    );

    // Calculate best value line - following Optuna's exact logic
    const best_value_line: Array<{
      x_value: number | string;
      best_value: number;
      trial_number: number;
    }> = [];

    let currentBest: number | null = null;

    // Sort ALL trials (not just feasible) by x_value to process in chronological order
    const sortedAllTrials = [...processedTrials].sort((a, b) => {
      if (typeof a.x_value === "number" && typeof b.x_value === "number") {
        return a.x_value - b.x_value;
      }
      return 0;
    });

    // Process each trial in chronological order
    for (let i = 0; i < sortedAllTrials.length; i++) {
      const trial = sortedAllTrials[i];

      // Only consider COMPLETE trials for best value tracking
      if (!trial || trial.state !== "COMPLETE" || typeof trial.value !== "number") {
        continue;
      }

      // Check if this is a new best value
      if (currentBest === null) {
        // First complete trial
        currentBest = trial.value;
        best_value_line.push({
          x_value: trial.x_value,
          best_value: currentBest,
          trial_number: trial.number,
        });
      } else {
        const isImprovement = isMinimization
          ? trial.value < currentBest
          : trial.value > currentBest;

        if (isImprovement) {
          // Add horizontal segment - extend previous best to just before this trial
          if (best_value_line.length > 0) {
            // Find the previous trial to create proper step
            const prevTrialIndex = i - 1;
            if (prevTrialIndex >= 0) {
              const prevTrial = sortedAllTrials[prevTrialIndex];
              if (prevTrial) {
                best_value_line.push({
                  x_value: prevTrial.x_value,
                  best_value: currentBest, // Keep the old best value
                  trial_number: prevTrial.number,
                });
              }
            }
          }

          // Update current best and add the improvement point
          currentBest = trial.value;
          best_value_line.push({
            x_value: trial.x_value,
            best_value: currentBest,
            trial_number: trial.number,
          });
        }
      }
    }

    // Extend the line to the very last trial (complete or not) maintaining current best
    if (currentBest !== null && sortedAllTrials.length > 0) {
      const lastTrial = sortedAllTrials[sortedAllTrials.length - 1];

      // Only add if it's not already the last point
      if (
        lastTrial &&
        best_value_line.length > 0 &&
        best_value_line[best_value_line.length - 1]?.x_value !==
          lastTrial.x_value
      ) {
        best_value_line.push({
          x_value: lastTrial.x_value,
          best_value: currentBest, // Maintain the current best, don't drop to zero!
          trial_number: lastTrial.number,
        });
      }
    }

    logger.info("OptimizationHistoryChart: Processed data", {
      totalSourceTrials: sourceTrials.length,
      sourceType: results?.trials ? "trials" : "top_trials",
      filteredTrials: trials.length,
      feasibleTrials: feasible_trials.length,
      infeasibleTrials: infeasible_trials.length,
      bestValuePoints: best_value_line.length,
      includePrunedSetting: includePruned,
    });

    return {
      feasible_trials,
      infeasible_trials,
      best_value_line,
    };
  }, [results, includePruned, includeNegativeValues, isMinimization]);

  // Build explicit XY series with metadata for tooltips
  const seriesFeasible = useMemo(
    () =>
      (Array.isArray(processedData?.feasible_trials) ? processedData.feasible_trials : [])
        .filter(
          (t) => t && typeof t.x_value === "number" && typeof t.value === "number",
        )
        .map((t) => ({
          x: t.x_value as number,
          y: t.value as number,
          // Keep metadata needed by tooltips
          number: t.number,
          state: t.state,
          datetime_start: t.datetime_start,
          datetime_complete: t.datetime_complete,
          duration: t.duration,
          params: t.params,
          state_color: t.state_color,
          state_label: t.state_label,
          is_best_so_far: t.is_best_so_far,
          improvement_pct: t.improvement_pct,
          cancellation_reason: t.cancellation_reason,
          rank: t.rank,
          completed_count: processedData.feasible_trials.length,
        })),
    [processedData.feasible_trials],
  );

  const seriesInfeasible = useMemo(
    () =>
      includePruned
        ? (Array.isArray(processedData?.infeasible_trials) ? processedData.infeasible_trials : [])
            .filter((t) => t && typeof t.x_value === "number")
            .map((t) => ({
              x: t.x_value as number,
              y: t.value || 0, // Use actual value if available for infeasible trials
              // Keep metadata needed by tooltips
              number: t.number,
              state: t.state,
              datetime_start: t.datetime_start,
              datetime_complete: t.datetime_complete,
              duration: t.duration,
              params: t.params,
              state_color: t.state_color,
              state_label: t.state_label,
              is_best_so_far: t.is_best_so_far,
              improvement_pct: t.improvement_pct,
              cancellation_reason: t.cancellation_reason,
              rank: t.rank, // undefined for infeasible
              completed_count: processedData.feasible_trials.length,
            }))
        : [],
    [processedData.infeasible_trials, includePruned, processedData.feasible_trials.length],
  );

  const seriesBestLine = useMemo(
    () =>
      (Array.isArray(processedData?.best_value_line) ? processedData.best_value_line : [])
        .filter(
          (p) =>
            p && typeof p.x_value === "number" && typeof p.best_value === "number",
        )
        .map((p) => ({
          x: p.x_value as number,
          y: p.best_value as number,
          trial_number: p.trial_number,
        })),
    [processedData.best_value_line],
  );

  // Chart-level data so axes always have a domain
  const chartData = useMemo(() => {
    const pts = [
      ...seriesFeasible,
      ...seriesInfeasible,
      ...seriesBestLine,
    ].filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    return pts.length ? pts.sort((a, b) => a.x - b.x) : [{ x: 0, y: 1 }]; // safe fallback
  }, [seriesFeasible, seriesInfeasible, seriesBestLine]);

  // Calculate Y-axis domain with padding
  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 1];
    
    const yValues = chartData.map(p => p.y).filter(y => Number.isFinite(y));
    if (yValues.length === 0) return [0, 1];
    
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    
    // Add 0.05 padding to the top as requested
    const paddedMaxY = maxY + 0.05;
    
    // Add some padding to the bottom as well for better visualization
    const range = maxY - minY;
    const paddedMinY = minY - (range * 0.05); // 5% padding at bottom
    
    return [paddedMinY, paddedMaxY];
  }, [chartData]);

  // Custom cursor component for crosshairs that follow mouse exactly
  const CustomCursor = useCallback((props: any) => {
    if (!mousePosition) return null;
    
    const { width, height } = props;
    const { x, y } = mousePosition;
    
    return (
      <g>
        {/* Vertical crosshair */}
        <line
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke="#9CA3AF"
          strokeDasharray="3 3"
          strokeOpacity={0.6}
          strokeWidth={1}
        />
        {/* Horizontal crosshair */}
        <line
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke="#9CA3AF"
          strokeDasharray="3 3"
          strokeOpacity={0.6}
          strokeWidth={1}
        />
      </g>
    );
  }, [mousePosition]);

  // Custom point renderer that respects marker size and highlighting
  const renderPoint = useCallback(
    (props: any) => {
      const { cx, cy, fill, stroke, payload } = props;
      if (typeof cx !== "number" || typeof cy !== "number") return null;

      const isHovered = hovered?.point?.number === payload?.number;

      return (
        <circle
          cx={cx}
          cy={cy}
          r={isHovered ? markerSize + 2 : markerSize}
          fill={fill}
          stroke={isHovered ? "#ffffff" : (stroke || "none")}
          strokeWidth={isHovered ? 2 : 1}
          opacity={isHovered ? 1 : 0.8}
          style={{
            filter: isHovered ? "drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))" : "none",
            transition: "all 0.2s ease-in-out",
            pointerEvents: "all", // ensure the dot itself receives pointer events
            cursor: "crosshair",
          }}
          // CRITICAL: Only consider a point "hovered" when the pointer is DIRECTLY over the circle
          onMouseEnter={() => setHovered({ cx, cy, point: payload })}
          onMouseLeave={() => setHovered(null)}
        />
      );
    },
    [markerSize, hovered],
  );

  // Custom tooltip component - show only when cursor is directly on a point
  const HistoryTooltip = ({ active, payload, onSize }: any) => {
    console.log('HistoryTooltip called:', { active, payload }); // Debug
    
    if (!active || !payload || payload.length === 0) return null;

    // Find scatter plot points (more permissive filtering for debugging)
    const trialItem = payload.find(
      (p: any) => p && p.payload && 
        (p.payload as any).number != null
        // Temporarily removed state and dataKey checks
    )?.payload as any;
    
    console.log('Found trialItem:', trialItem); // Debug
    
    // Only show tooltip if we found a valid trial point
    if (!trialItem) return null;

    const trialNo = trialItem.number ?? "N/A";
    const value = typeof trialItem.y === "number" ? trialItem.y : 
                  typeof trialItem.value === "number" ? trialItem.value : "N/A";
    const rank: number | undefined = typeof trialItem.rank === "number" ? trialItem.rank : undefined;
    const completedCount: number | undefined =
      typeof trialItem.completed_count === "number" ? trialItem.completed_count : undefined;

    // Optional timestamps
    const start = trialItem.datetime_start
      ? new Date(trialItem.datetime_start)
      : null;
    const done = trialItem.datetime_complete
      ? new Date(trialItem.datetime_complete)
      : null;
    const duration = typeof trialItem.duration === "number"
      ? `${trialItem.duration.toFixed(1)}s`
      : null;

    // --- Measure size AFTER commit to avoid setState during render ---
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const lastSizeRef = React.useRef<{ width: number; height: number } | null>(null);
    React.useLayoutEffect(() => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const size = { width: Math.round(rect.width), height: Math.round(rect.height) };
      const prev = lastSizeRef.current;
      if (!prev || prev.width !== size.width || prev.height !== size.height) {
        lastSizeRef.current = size;
        onSize?.(size);
      }
      // dependencies: re-measure if content changes
    }, [active, trialNo, value, rank, completedCount, duration, start?.getTime(), done?.getTime(), onSize]);

    return (
      <div
        ref={containerRef}
        className="rounded-lg border border-dark-700 bg-dark-900/90 px-3 py-2 text-xs shadow-lg"
      >
        <div className="font-semibold text-brand-blue mb-1">
          <div className="flex items-center gap-2">
            <span>Trial #{trialNo}</span>
            {trialItem.state && trialItem.state !== "COMPLETE" && (
              <span 
                className="px-1.5 py-0.5 rounded text-xs font-medium" 
                style={{ backgroundColor: trialItem.state_color + '20', color: trialItem.state_color }}
              >
                {trialItem.state_label}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-0.5">
          <div>
            <span className="text-muted-foreground">Sortino Ratio:</span>{" "}
            <span className="font-mono">
              {typeof value === "number" ? value.toFixed(6) : value}
            </span>
          </div>
          {typeof rank === "number" && (
            <div>
              <span className="text-muted-foreground">Rank:</span>{" "}
              <span className="font-mono">
                {toOrdinal(rank)}
                {typeof completedCount === "number" ? ` of ${completedCount}` : ""}
              </span>
            </div>
          )}
          {start && (
            <div>
              <span className="text-muted-foreground">Start:</span>{" "}
              {start.toLocaleString()}
            </div>
          )}
          {done && (
            <div>
              <span className="text-muted-foreground">End:</span>{" "}
              {done.toLocaleString()}
            </div>
          )}
          {duration && (
            <div>
              <span className="text-muted-foreground">Duration:</span>{" "}
              {duration}
            </div>
          )}
          {trialItem.params && Object.keys(trialItem.params).length > 0 && (
            <div className="pt-1">
              <div className="text-muted-foreground">Params:</div>
              <div className="grid grid-cols-2 gap-x-4">
                {Object.entries(trialItem.params)
                  .slice(0, 6)
                  .map(([k, v]) => (
                    <div key={k}>
                      <span className="text-muted-foreground">
                        {getParameterDisplayName(k)}:
                      </span>{" "}
                      <span className="font-mono">
                        {formatParameterValue(
                          k,
                          v as string | number | boolean,
                        ).replace(/<[^>]*>/g, "")}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {trialItem.is_best_so_far && (
            <div className="flex items-center gap-1 text-xs text-emerald-400 pt-1">
              <TrendingUp className="h-3 w-3" />
              Best so far ({trialItem.improvement_pct.toFixed(1)}% improvement)
            </div>
          )}
          {trialItem.cancellation_reason && (
            <div className="text-xs text-amber-400 pt-1 border-t border-dark-600 mt-2">
              <span className="text-muted-foreground">Reason:</span>{" "}
              {trialItem.cancellation_reason}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Calculate stats for display
  const stats = useMemo(() => {
    const sourceTrials = results?.trials || results?.top_trials || [];
    if (!Array.isArray(sourceTrials)) return { total: 0, complete: 0, pruned: 0, failed: 0, negative: 0 };

    const complete = sourceTrials.filter(t => t?.state === "COMPLETE").length;
    const pruned = sourceTrials.filter(t => t?.state === "PRUNED").length;
    const failed = sourceTrials.filter(t => t?.state === "FAIL").length;
    const negative = sourceTrials.filter(
      t => t?.state === "COMPLETE" && typeof t?.value === "number" && t.value < 0
    ).length;

    return { total: sourceTrials.length, complete, pruned, failed, negative };
  }, [results]);


  return (
    <div className={cn("space-y-0", className)}>
      {/* Unified Chart Card with Integrated Toolbar */}
      <Card className="bg-dark-900/50 border-dark-700 hover:shadow-glow transition-all duration-300 overflow-hidden">
        {/* Compact Header */}
        <CardHeader className="pb-0 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-blue/10">
                <Activity className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <CardTitle className="text-base">Optimization Progress</CardTitle>
                <CardDescription className="text-xs">
                  By trial number
                </CardDescription>
              </div>
            </div>
            {/* Quick Stats Badge */}
            <div className="flex items-center gap-2">
              <div className="px-3 py-1.5 rounded-full bg-dark-800/80 border border-dark-600 text-xs font-medium">
                <span className="text-emerald-400">{seriesFeasible.length}</span>
                <span className="text-muted-foreground"> / {stats.total} trials</span>
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Integrated Toolbar */}
        <div className="px-6 py-4">
          <div className="p-3 rounded-xl bg-dark-800/40 border border-dark-700/50 space-y-3">
            {/* Row 1: Trials Slider */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Trials Slider - Compact */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Trials</span>
                <div className="flex-1 min-w-[100px]">
                  <Slider
                    value={[pointLimit]}
                    onValueChange={(v) => onPointLimitChange?.(v[0] ?? 100)}
                    min={1}
                    max={totalTrialsAvailable}
                    step={1}
                    className="w-full"
                  />
                </div>
                <span className="text-xs font-mono text-brand-blue whitespace-nowrap">{pointLimit}</span>
              </div>
            </div>

            {/* Row 2: Filters + Marker Size + Stats */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-dark-700/50">
              {/* Filters */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Show</span>
                <div className="flex items-center gap-1.5">
                  {/* Pruned Toggle Pill */}
                  <button
                    onClick={() => setIncludePruned(!includePruned)}
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border",
                      includePruned
                        ? "bg-slate-500/15 border-slate-500/40 text-slate-300 shadow-[0_0_10px_rgba(100,116,139,0.15)]"
                        : "bg-dark-900/60 border-dark-600 text-muted-foreground hover:border-dark-500 hover:text-foreground"
                    )}
                  >
                    <Scissors className="h-3 w-3" />
                    Pruned
                    {stats.pruned > 0 && (
                      <span className={cn(
                        "ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold",
                        includePruned ? "bg-slate-500/30" : "bg-dark-700"
                      )}>
                        {stats.pruned}
                      </span>
                    )}
                  </button>

                  {/* Negative Toggle Pill */}
                  <button
                    onClick={() => setIncludeNegativeValues(!includeNegativeValues)}
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border",
                      includeNegativeValues
                        ? "bg-amber-500/15 border-amber-500/40 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.15)]"
                        : "bg-dark-900/60 border-dark-600 text-muted-foreground hover:border-dark-500 hover:text-foreground"
                    )}
                  >
                    <TrendingDown className="h-3 w-3" />
                    Negative
                    {stats.negative > 0 && (
                      <span className={cn(
                        "ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold",
                        includeNegativeValues ? "bg-amber-500/30" : "bg-dark-700"
                      )}>
                        {stats.negative}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Marker Size - Compact */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Size</span>
                <div className="w-20">
                  <Slider
                    value={[markerSize]}
                    onValueChange={(v) => setMarkerSize(v[0] ?? 6)}
                    max={12}
                    min={2}
                    step={1}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-6 bg-dark-600" />

              {/* Inline Stats */}
              <div className="flex items-center gap-3 text-xs">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 text-emerald-400">
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span className="font-medium">{stats.complete}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Complete trials</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 text-slate-400">
                        <div className="w-2 h-2 rounded-full bg-slate-400" />
                        <span className="font-medium">{stats.pruned}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p>Pruned trials</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {stats.failed > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 text-red-400">
                          <div className="w-2 h-2 rounded-full bg-red-400" />
                          <span className="font-medium">{stats.failed}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Failed trials</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {stats.negative > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 text-amber-400">
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          <span className="font-medium">{stats.negative}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Negative Sortino values</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chart Area */}
        <CardContent className="pt-0 pb-4">
          <div ref={chartRef} className="relative h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart 
                data={chartData}
                onMouseMove={(e: any) => {
                  // Only update crosshair position; do NOT infer hover here
                  if (e && e.chartX !== undefined && e.chartY !== undefined) {
                    setMousePosition({ x: e.chartX, y: e.chartY });
                  }
                }}
                onMouseLeave={() => {
                  setMousePosition(null);
                  setHovered(null);
                }}>              
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.3}
                />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={["dataMin", "dataMax"]}
                  stroke="#9CA3AF"
                  fontSize={12}
                  tickFormatter={(value) => `#${Math.round(value)}`}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  domain={yAxisDomain}
                  scale="linear"
                  stroke="#9CA3AF"
                  fontSize={12}
                  allowDataOverflow
                  label={{
                    value: "Sortino Ratio",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <RechartsTooltip
                  // Fully controlled: show ONLY when a dot is hovered
                  active={!!hovered}
                  position={hovered ? computeTooltipPosition(hovered.cx, hovered.cy) : { x: 0, y: 0 }}
                  // Provide a payload compatible with your HistoryTooltip expectations
                  content={() =>
                    hovered ? (
                      <HistoryTooltip
                        active={true}
                        payload={[{ payload: hovered.point }]}
                        onSize={updateTooltipSize}
                      />
                    ) : null
                  }
                  cursor={<CustomCursor />}
                  isAnimationActive={false}
                  wrapperStyle={{ pointerEvents: "none", zIndex: 40 }}
                />
                <Legend />

                {/* Feasible trials */}
                <Scatter
                  name="Complete Trials"
                  data={seriesFeasible}
                  fill="#3b82f6"
                  shape={renderPoint as any}
                  isAnimationActive={false}
                />

                {/* Infeasible trials (pruned, failed, etc) */}
                {includePruned && seriesInfeasible.length > 0 && (
                  <Scatter
                    name="Pruned/Failed Trials"
                    data={seriesInfeasible}
                    fill="#64748b"
                    shape={renderPoint as any}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                  />
                )}

                {/* Best value line */}
                {seriesBestLine.length > 0 && (
                  <Line
                    type="stepAfter"
                    data={seriesBestLine}
                    dataKey="y"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={false}
                    isAnimationActive={false}
                    name="Best Value"
                    activeDot={false}
                    connectNulls={false}
                  />
                )}

                {/* Best value reference line */}
                {results?.best_value && typeof results.best_value === "number" && (
                  <ReferenceLine
                    y={results.best_value}
                    stroke="#10b981"
                    strokeDasharray="8 8"
                    strokeOpacity={0.7}
                    label={{
                      value: `Best: ${results.best_value.toFixed(4)}`,
                      position: "top",
                      style: { fill: "#10b981", fontSize: "12px" },
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
